import { Injectable } from '@angular/core';
import { MapService } from '../map.service';
import BaseLayer from 'ol/layer/Base';
import olLayer from 'ol/layer/Layer';
import olMap from 'ol/Map';
import { Layer } from '../../models/layer';
import { Group as OlLayerGroup } from 'ol/layer';
import { MaskLayer } from '../../models/mask-layer';

/**
 * * This service handles the Openlayers methods
 */

@Injectable({
  providedIn: 'root',
})
export class OlLayerService {
  constructor(private mapService: MapService) { }

  /*** GETTERS ***/

/** * Returns all instances of a layer across all active maps.
 * Useful for toggling visibility or opacity on both maps at once.
 */
getLayersById(id: string): BaseLayer[] {
  const foundLayers: BaseLayer[] = [];

  this.mapService._maps.forEach((map: olMap) => {
    // Helper to search layers recursively (handles groups)
    const findInCollection = (collection: any) => {
      collection.forEach((lyr: any) => {
        if (lyr.get('customId') === id) {
          foundLayers.push(lyr);
        }
        if (lyr instanceof OlLayerGroup) {
          findInCollection(lyr.getLayers());
        }
      });
    };

    findInCollection(map.getLayers());
  });
  return foundLayers;
}

  getLayerById(id: string): BaseLayer {
    let layer;
    this.mapService
      .getMap()
      .getLayers()
      .forEach((lyrOrGrp) => {
        if (lyrOrGrp instanceof OlLayerGroup) {
          lyrOrGrp.getLayers().forEach((lyr) => {
            if (lyr.get('customId') === id) {
              layer = lyr;
            }
          });
        } else if (lyrOrGrp.get('customId') === id) {
          layer = lyrOrGrp;
        }
      });

    return layer;
  }

  getLayerOrGroupById(id: string): BaseLayer {
    let layer: any = null;

    this.mapService
      .getMap()
      .getLayers()
      .forEach((lyrOrGrp: any) => {
        if (lyrOrGrp.get('customId') === id) {
          layer = lyrOrGrp;
        }
      });

    return layer;
  }

  isGroupLayer(layer) {
    return (layer instanceof OlLayerGroup);
  }

  getLayerSource(customId: string) {
    const olLayer = this.findEnabledOlLayer(customId);
    return olLayer.getSource();
  }

  /*** INITIALIZATIONS ***/

  addBaselayers(businessLayers: Layer[]): void {
    businessLayers.forEach((baselayer) => {
      this.addLayerToMap(baselayer, 'baselayers');
    });
  }

  addMasklayers(businessMaskLayers: MaskLayer[]): void {
    businessMaskLayers.forEach((maskLayer) =>
      this.addLayerToMap(maskLayer, 'masklayers')
    );
  }

  /*** OPERATIONS ***/

  addLayerToMap(layer: Layer, groupId?: string): void {
    if (this.findEnabledOlLayer(layer.id) != undefined) {
      this.updateLayerInMap(this.findEnabledOlLayer(layer.id), layer);
    } else {
      const olLayer = layer.toOlLayer();
      if (groupId) {
        olLayer.set('groupId', groupId);
      }
      this.mapService.getMap().addLayer(olLayer);
    }
  }

  addOlLayerToMap(layer: olLayer, mapIds?: string | string[]): void {
    const ids = ([] as string[]).concat(mapIds ?? []);
    if (ids.length === 0) {
      this.mapService.getMap().addLayer(layer);
      return;
    }
    ids.forEach(id => this.mapService.getMap(id).addLayer(layer));
  }

  updateLayerInMap(oldLayer: BaseLayer, newLayer: Layer): void {
    // * We're changing the old layer source
    oldLayer.set('source', newLayer.toOlLayer().getSource());
  }

  // removeLayerFromMap(layer: Layer): void {
  //   this.mapService
  //     .getMap()
  //     .getLayers()
  //     .forEach((lyr) => {
  //       if (lyr && lyr.getProperties().customId === layer.id) {
  //         this.mapService.getMap().removeLayer(lyr);
  //       }
  //     });
  // }

  // removeOlLayerFromMap(layer: olLayer): void {
  //   this.mapService.getMap().removeLayer(layer);
  // }


  removeLayerFromMap(layer: Layer, mapIds?: string | string[]): void {
    const ids: string[] = mapIds == null ? [] : Array.isArray(mapIds) ? mapIds : [mapIds];
    if (ids.length === 0) {
      //default map
      this.removeLayerFromSingleMap(layer, 'main');
      return;
    }
    ids.forEach(id => {
      this.removeLayerFromSingleMap(layer, id);
    });
  }

  // removeOlLayerFromMap(layer: olLayer): void {
  //   this.mapService.getMap().removeLayer(layer);
  // }

  removeOlLayerFromMap(layer: olLayer, mapIds?: string | string[]): void {
    const ids: string[] = mapIds == null ? [] : Array.isArray(mapIds) ? mapIds : [mapIds];
    if (ids.length === 0) {
      this.mapService.getMap().removeLayer(layer);
      return;
    }
    ids.forEach(id => {
      const map = this.mapService.getMap(id);
      this.mapService.getMap(id).removeLayer(layer);
    });
  }

  private removeLayerFromSingleMap(layer: Layer, mapId): void {
    this.mapService
      .getMap(mapId)
      .getLayers()
      .forEach((lyr) => {
        if (lyr && lyr.getProperties().customId === layer.id) {
          this.mapService.getMap().removeLayer(lyr);
        }
      });
  }


  setZIndex(layer: Layer, index: number) {
    const olLayer = this.findEnabledOlLayer(layer.id);
    if (olLayer) {
      olLayer.setZIndex(index);
    }
  }

  setOpacity(layer: Layer, opacity: number) {
    const olLayer: BaseLayer = this.findEnabledOlLayer(layer.id);
    olLayer.setOpacity(opacity);
  }

  setVisible(layer: Layer, visible: boolean) {
    const olLayer: BaseLayer = this.findEnabledOlLayer(layer.id);
    olLayer.setVisible(visible);
  }

  displaySelectedBaselayer(selectedLayer: Layer) {
    let baseLayersCollection: BaseLayer[] = this.getOlBaselayers();
    baseLayersCollection.forEach((layer: BaseLayer) => {
      return layer.setVisible(layer.get('customId') === selectedLayer.id);
    });
  }
  
  getOlBaselayers(): BaseLayer[] | any {
    let baseLayersCollection: BaseLayer[] = new Array();

    this.mapService
      .getMap()
      .getLayers()
      .forEach((layer) => {
        if (layer.getProperties().groupId === 'baselayers') {
          baseLayersCollection.push(layer);
        }
      });
    return baseLayersCollection;
  }

  getOlEnabledLayers(): BaseLayer[] {
    let enabledLayersCollection: BaseLayer[] = new Array();

    this.mapService
      .getMap()
      .getLayers()
      .forEach((layer) => {
        if (layer.getProperties().groupId === 'layers') {
          enabledLayersCollection.push(layer);
        }
      });
    return enabledLayersCollection;
  }

  /*** SEARCH ***/

  findEnabledOlLayer(layerId: string): olLayer {
    let olLayer: any;
    this.getOlEnabledLayers().forEach((lyr) => {
      if (lyr.get('customId') === layerId) {
        olLayer = lyr;
      }
    });
    return olLayer;
  }
}
